'use client'

import { Menu, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { MouseEvent, ReactNode } from 'react'

import { useFramePath } from './config'

/**
 * The sidebar on narrow screens: a menu button that opens the same navigation
 * as a drawer from the left, as the design system prescribes for phones.
 *
 * A native <dialog> opened with showModal() rather than a positioned div: it
 * sits in the top layer, so no transformed ancestor can clip it, it makes the
 * page behind it inert, keeps focus inside, and closes on Escape on its own.
 * It closes again when the route changes, i.e. after a link was followed.
 */
export function MobileNav({
  label,
  openLabel,
  closeLabel,
  brand,
  footer,
  children
}: {
  /** Accessible name of the drawer, e.g. "Hauptmenü". */
  label: string
  openLabel: string
  closeLabel: string
  brand: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const pathname = useFramePath()

  useEffect(() => {
    ref.current?.close()
  }, [pathname])

  // A click on the backdrop lands on the dialog element itself; one inside the
  // panel lands on a descendant.
  function onDialogClick(e: MouseEvent<HTMLDialogElement>): void {
    if (e.target === e.currentTarget) e.currentTarget.close()
  }

  return (
    <>
      <button
        type="button"
        aria-label={openLabel}
        title={openLabel}
        aria-haspopup="dialog"
        className="av-iconbtn av-iconbtn--md av-iconbtn--secondary"
        onClick={() => ref.current?.showModal()}
      >
        <Menu className="size-4" />
      </button>
      <dialog ref={ref} aria-label={label} className="av-mnav" onClick={onDialogClick}>
        <div className="av-side av-mnav__panel">
          <div className="av-side__brand">
            {brand}
            <button
              type="button"
              aria-label={closeLabel}
              title={closeLabel}
              className="av-iconbtn av-iconbtn--sm av-iconbtn--ghost"
              onClick={() => ref.current?.close()}
            >
              <X className="size-4" />
            </button>
          </div>
          <nav aria-label={label} className="av-side__nav">
            {children}
          </nav>
          {footer}
        </div>
      </dialog>
    </>
  )
}
